import { tryFloorConvexHull } from '../../../facets/floor-footprint';
import {createLabelMeasurementContext} from '../../Label3D';

import { THREE } from "@adsk/lmv";

const IDLE_FRAME_BUDGET = 2; // If under this ms, don't wait for next idle frame.
export const LABEL_HEIGHT_PX = 22; // matches label component height (incl padding) but minus a bit
const LEVEL_LABEL_FONT = '300 16px ArtifaktElement'; // match css
const MAX_LVL_MESH_TRIES = 3; // Number of times to replace is idle load queue if incomplete.

export class LevelLabelPlacer {
    layer;

    /** Hull states, includes details about completion, settling, hull, and minimum Z. */
    hulls = new Map();
    loadQueue = [];
    idsInQueue = new Set();

    constructor(levelsLayer, onAllLoaded) {
        this.layer = levelsLayer;
        this.onAllLoaded = onAllLoaded;

        this.measureCtx = createLabelMeasurementContext(levelsLayer.viewer);
        this.measureCtx.font = LEVEL_LABEL_FONT;
    }

    addToIdleCacheQueue(facility, level) {
        if (this.idsInQueue.has(level.id)) return;

        this.idsInQueue.add(level.id);
        const length = this.loadQueue.push({ id: level.id, tries: 0 });
        if (length === 1) {
            this.#processQueueItem(facility);
        }
    }

    invalidateHulls() {
        this.hulls.clear();
        this.loadQueue.length = 0;
        this.idsInQueue.clear();
    }

    async #processQueueItem(facility, budget = 0) {

        if (budget <= 0) {
            // Out of allowance, wait for next idle frame.
            await Promise.all([
                facility.viewer.impl.waitForLoaderIdle(),
                facility.viewer.impl.waitForRendererIdle(),
                new Promise((resolve) => requestAnimationFrame(resolve)),
            ]);
            budget = IDLE_FRAME_BUDGET;

			if (this.loadQueue.length === 0) {
				// hulls were invalidated while awaiting, exit without triggering completion. Likely twin switching.
				return;
			}
        }

        const item = this.loadQueue.shift();
        // Use the same source the layer labels from (Location levels for LBS, else the Levels facet),
        // so the floor hull is computed for the right node.
        const levelsFacets = facility.facetsManager.getHudLevelNodes().nodes;
        const level = levelsFacets.find(mn => mn.id === item.id);

        let delta = 0;

        if (level) {
            const s0 = performance.now();
            const priorState = this.hulls.get(item.id) ?? {};
            const updatedState = tryFloorConvexHull(facility, level, priorState)
            this.hulls.set(item.id, updatedState);

            // Not finished, maybe still loading.
            //TODO: Not sure that if it fails once it will succeed later -- waiting for geometry loaded events
            //should be deterministic given we only react to model added events from the facets manager
            if (!updatedState.isComplete && item.tries < MAX_LVL_MESH_TRIES) {
                item.tries += 1;
                this.loadQueue.push(item);
            } else {
                updatedState.settled = true;
                this.idsInQueue.delete(item.id);
            }

            delta = performance.now() - s0;
        } else {
            //Case where the model for this filter item is currently off, so the facet doesn't exist at runtime (yet)
            this.idsInQueue.delete(item.id);
        }

        // console.info('Convex hull for level ' + level.id + ' took ' + delta + 'ms');

        // Launch next.
        if (this.loadQueue.length) {
            this.#processQueueItem(facility, budget - delta);
        } else {
            // Readjust all labels once the queue has been processed.
            // console.info('All level cache processed');
            this.onAllLoaded?.();
        }
    }

    guessPlacement(facility, level, avgElev, useCacheOnly) {
        const state = this.hulls.get(level.id);
        if (state?.hull) {
            // Calculate the best placement from a loaded floor highlight cache mesh.
            const placement = guessPlacementByFloorHull(facility, level, avgElev ?? state.minZ, state.hull);
            if (placement) return placement;
        }

        if (!useCacheOnly) {
            // Register level cache to be loaded.
            this.addToIdleCacheQueue(facility, level);
        }
    }
}

/** Finds the left and right most point of the floor's convex hull based on screen coordinates. */
function guessPlacementByFloorHull(facility, level, elevation, hull) {

    // Some level have geometry get the necessary data in place
    const viewerImpl = facility.viewer.impl;
    let minX = Infinity, minVal = new THREE.Vector3();
    let maxX = -Infinity, maxVal = new THREE.Vector3();

    const p = new THREE.Vector3(0, 0, elevation);
    for (const pt of hull) {
        p.x = pt.x;
        p.y = pt.y;
        const {x} = viewerImpl.worldToClient(p);

        // Find left and right
        if (x < minX) {
            minX = x;
            minVal.copy(p);
        }
        if (x > maxX) {
            maxX = x;
            maxVal.copy(p);
        }
    }

    // Provide both left & right-most vertex to let the caller choose.
    const left = new THREE.Vector3(minVal.x, minVal.y, elevation);
    const right = new THREE.Vector3(maxVal.x, maxVal.y, elevation);
    return { left, right };
}
